<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import AiKnowledgeSourcesAPI from 'dashboard/api/aiKnowledgeSources';
import AddAiKnowledgeSourceSidePanel from './AddAiKnowledgeSourceSidePanel.vue';

defineProps({
  showAddButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add']);

const { t } = useI18n();

const sources = ref([]);
const isLoading = ref(false);
const showAddPanel = ref(false);

const loadSources = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiKnowledgeSourcesAPI.get();
    sources.value = data;
  } catch {
    useAlert(t('AI_AGENTS.KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openAddPanel = () => {
  showAddPanel.value = true;
  emit('add');
};

const removeSource = async id => {
  try {
    await AiKnowledgeSourcesAPI.delete(id);
    useAlert(t('AI_AGENTS.KNOWLEDGE.DELETE_SUCCESS'));
    await loadSources();
  } catch {
    useAlert(t('AI_AGENTS.KNOWLEDGE.SAVE_ERROR'));
  }
};

const syncSource = async id => {
  try {
    await AiKnowledgeSourcesAPI.sync(id);
    useAlert(t('AI_AGENTS.KNOWLEDGE.SYNC_SUCCESS'));
    await loadSources();
  } catch {
    useAlert(t('AI_AGENTS.KNOWLEDGE.SAVE_ERROR'));
  }
};

const onSourceCreated = () => {
  loadSources();
};

defineExpose({ openAddPanel, loadSources });

onMounted(loadSources);
</script>

<template>
  <div class="grid min-w-0 gap-4">
    <div
      v-if="showAddButton"
      class="flex flex-wrap items-center justify-end gap-3"
    >
      <Button
        :label="t('AI_AGENTS.KNOWLEDGE.ADD_SOURCE')"
        icon="i-lucide-plus"
        size="sm"
        @click="openAddPanel"
      />
    </div>

    <woot-loading-state
      v-if="isLoading"
      :message="t('AI_AGENTS.KNOWLEDGE.LOADING')"
    />

    <ul v-else-if="sources.length" class="m-0 grid list-none gap-2 p-0">
      <li
        v-for="source in sources"
        :key="source.id"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-background p-4"
      >
        <div class="grid min-w-0 gap-1">
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ source.name }}
          </p>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              source.source_type === 'file'
                ? source.file_name || t('AI_AGENTS.KNOWLEDGE.TYPE_FILE')
                : source.source_url
            }}
            ·
            {{
              t('AI_AGENTS.KNOWLEDGE.CHUNKS_COUNT', {
                count: source.chunks_count,
              })
            }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            :label="t('AI_AGENTS.KNOWLEDGE.SYNC')"
            size="sm"
            slate
            faded
            @click="syncSource(source.id)"
          />
          <Button
            :label="t('AI_AGENTS.KNOWLEDGE.DELETE')"
            size="sm"
            ruby
            faded
            @click="removeSource(source.id)"
          />
        </div>
      </li>
    </ul>

    <div
      v-else
      class="grid justify-items-center gap-3 rounded-lg border border-dashed border-n-weak px-6 py-12 text-center"
    >
      <span class="text-4xl i-lucide-book-open text-n-slate-10" />
      <p class="m-0 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.KNOWLEDGE.EMPTY_ACCOUNT') }}
      </p>
      <Button
        :label="t('AI_AGENTS.KNOWLEDGE.ADD_SOURCE')"
        icon="i-lucide-plus"
        outline
        slate
        sm
        @click="openAddPanel"
      />
    </div>

    <AddAiKnowledgeSourceSidePanel
      :open="showAddPanel"
      @close="showAddPanel = false"
      @created="onSourceCreated"
    />
  </div>
</template>
